import { apiClient } from './apiClient';

export interface AppSetting {
  key: string;
  value: string;
  type: 'string' | 'number' | 'boolean' | 'file' | 'json';
  category: string;
}

export interface SettingsResponse {
  [key: string]: {
    value: string;
    type: string;
    category: string;
    url?: string;
    is_default?: boolean;
  };
}

export interface SettingsUpdateRequest {
  settings: {
    [key: string]: {
      value: string;
      type?: string;
      category?: string;
    } | string;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

class SettingsService {
  private readonly baseUrl = '/settings';

  /**
   * Get all settings
   */
  async getSettings(): Promise<SettingsResponse> {
    const response = await apiClient.get<ApiResponse<SettingsResponse>>(`${this.baseUrl}?_t=${Date.now()}`);
    return response.data;
  }

  /**
   * Get a specific setting
   */
  async getSetting(key: string): Promise<AppSetting> {
    const response = await apiClient.get<ApiResponse<AppSetting>>(`${this.baseUrl}/${key}`);
    return response.data;
  }

  /**
   * Update settings
   */
  async updateSettings(settings: SettingsUpdateRequest['settings']): Promise<void> {
    await apiClient.post<ApiResponse<void>>(`${this.baseUrl}`, { settings });
  }

  /**
   * Update a single setting
   */
  async updateSetting(key: string, value: string, type: string = 'string', category: string = 'general'): Promise<void> {
    await this.updateSettings({
      [key]: {
        value,
        type,
        category
      }
    });
  }

  /**
   * Upload favicon
   */
  async uploadFavicon(file: File): Promise<{ path: string; url: string }> {
    const formData = new FormData();
    formData.append('favicon', file);

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    
    // Use axios directly for file upload with custom headers
    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/favicon`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload favicon');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Upload site logo
   */
  async uploadLogo(file: File): Promise<{ path: string; url: string }> {
    const formData = new FormData();
    formData.append('logo', file);

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    // Use axios directly for file upload with custom headers
    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/logo`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload logo');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get default settings structure
   */
  getDefaultSettings(): { [key: string]: { value: string; type: string; category: string; label: string; description?: string } } {
    return {
      site_name: {
        value: 'NextGen - Shop',
        type: 'string',
        category: 'general',
        label: 'Site Name',
        description: 'The name of your store that appears in the title and sidebar'
      },
      site_description: {
        value: 'Management System & POS',
        type: 'string',
        category: 'general',
        label: 'Site Description',
        description: 'A brief description of your store'
      },
      site_logo: {
        value: '',
        type: 'file',
        category: 'appearance',
        label: 'Site Logo',
        description: 'Logo displayed in the sidebar and header'
      },
      site_favicon: {
        value: '',
        type: 'file',
        category: 'appearance',
        label: 'Favicon',
        description: 'Small icon displayed in browser tabs'
      },
      primary_color: {
        value: '#3B82F6',
        type: 'string',
        category: 'appearance',
        label: 'Primary Color',
        description: 'Main theme color used throughout the application'
      },
      currency_symbol: {
        value: '₨',
        type: 'string',
        category: 'general',
        label: 'Currency Symbol',
        description: 'Symbol displayed for prices and amounts'
      },
      timezone: {
        value: 'Asia/Karachi',
        type: 'string',
        category: 'general',
        label: 'Timezone',
        description: 'Timezone for dates and times'
      },
      low_stock_threshold: {
        value: '10',
        type: 'number',
        category: 'inventory',
        label: 'Low Stock Threshold',
        description: 'Quantity below which products are considered low stock'
      },
      expiry_alert_days: {
        value: '30',
        type: 'number',
        category: 'inventory',
        label: 'Expiry Alert Days',
        description: 'Number of days before expiry to show alerts'
      },
      backup_frequency: {
        value: 'daily',
        type: 'string',
        category: 'system',
        label: 'Backup Frequency',
        description: 'How often to automatically backup data'
      },
      max_file_size: {
        value: '10240',
        type: 'number',
        category: 'system',
        label: 'Max File Size (KB)',
        description: 'Maximum file size for uploads'
      },
      print_store_name: {
        value: 'NextGen - Shop',
        type: 'string',
        category: 'print',
        label: 'Store Name (Print)',
        description: 'Store name to appear on receipts and reports'
      },
      print_phone: {
        value: '+92 348 119 6365',
        type: 'string',
        category: 'print',
        label: 'Store Phone (Print)',
        description: 'Phone number to appear on receipts'
      },
      print_address: {
        value: '123 Main Street, Suite 456, Springfield, USA 12345',
        type: 'string',
        category: 'print',
        label: 'Store Address (Print)',
        description: 'Address to appear on receipts'
      }
    };
  }
}

export const settingsService = new SettingsService();
